import { useState, useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY = 'admin_last_activity';
const THROTTLE_MS = 1000;

const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [role="button"], [role="link"], [role="tab"], [contenteditable="true"]';

function getStoredLastActivity() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? parseInt(v, 10) : null;
  } catch {
    return null;
  }
}

function setStoredLastActivity(ts) {
  try {
    localStorage.setItem(STORAGE_KEY, String(ts));
  } catch {}
}

/** Kliknięcie w realny element interaktywny (link, przycisk, pole), nie w puste pole. */
function isMeaningfulClick(ev) {
  const t = ev && ev.target;
  if (!t || !t.closest) return false;
  return !!t.closest(INTERACTIVE_SELECTOR);
}

/** Wciśnięcie klawisza podczas wpisywania w pole (input/textarea/contenteditable). */
function isMeaningfulKeydown(ev) {
  const t = ev && ev.target;
  if (!t || !t.tagName) return false;
  const tag = t.tagName.toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  if (t.getAttribute && t.getAttribute('contenteditable') === 'true') return true;
  return false;
}

/**
 * Hook: wylogowanie po timeoutMs bezczynności.
 * Licznik resetuje się tylko przy realnym kliknięciu w opcję (link, przycisk, pole) lub wpisywaniu w pole.
 * @param {number} timeoutMs - czas w ms (np. 30 * 60 * 1000)
 * @param {() => void} onLogout - callback wylogowania
 * @returns {{ secondsRemaining: number | null }} - pozostałe sekundy do wylogowania (null gdy nie aktywny)
 */
export function useInactivityLogout(timeoutMs, onLogout) {
  const [secondsRemaining, setSecondsRemaining] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const lastThrottleRef = useRef(0);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  const refreshActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottleRef.current < THROTTLE_MS) return;
    lastThrottleRef.current = now;
    lastActivityRef.current = now;
    setStoredLastActivity(now);
  }, []);

  useEffect(() => {
    const stored = getStoredLastActivity();
    const now = Date.now();
    if (stored != null && now - stored > timeoutMs) {
      onLogoutRef.current();
      return;
    }
    lastActivityRef.current = stored != null ? stored : now;
    if (stored == null) setStoredLastActivity(now);

    const handleClick = (ev) => {
      if (isMeaningfulClick(ev)) refreshActivity();
    };
    const handleKeydown = (ev) => {
      if (isMeaningfulKeydown(ev)) refreshActivity();
    };

    window.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKeydown);

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      const remainingMs = timeoutMs - elapsed;
      if (remainingMs <= 0) {
        setStoredLastActivity(now);
        onLogoutRef.current();
        return;
      }
      setSecondsRemaining(Math.floor(remainingMs / 1000));
    }, 1000);

    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeydown);
      clearInterval(interval);
    };
  }, [timeoutMs, refreshActivity]);

  return { secondsRemaining };
}

export function clearInactivityStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/** Ustaw ostatnią aktywność na teraz (np. po logowaniu). */
export function setLastActivityNow() {
  setStoredLastActivity(Date.now());
}
